<template>
  <thead>
    <tr
      class="text-xs"
      :class="[hideHeaders ? 'h-0 bg-none' : 'h-12 max-h-12 px-2 py-4 bg-n-10 dark:bg-n-700']"
    >
      <th v-if="hasSortableRows" class="w-8"></th>
      <th v-if="hasMultiSelect" class="w-8"></th>
      <th
        v-for="column of columns"
        :key="column.key"
        class="font-normal text-n-800 dark:text-n-0"
        :class="[column.tailwindWidth]"
        :style="{
          width: !column.tailwindWidth ? column.width || 'auto' : undefined
        }"
      >
        <slot
          :name="`head(${column.key})`"
          :column="column"
          :merged-headers="getMergedHeadersForColumn?.(column.key) ?? []"
          v-if="!hideHeaders"
        >
          <template v-if="Object.keys(parentSlots).includes(`head(${column.key})`)">
            <template v-for="(vnode, vnodeIdx) in renderParentHeadSlot(column)" :key="vnodeIdx">
              <component :is="vnode" />
            </template>
          </template>
          <l-table-header-cell
            v-else
            :sort-direction="sorting[column.key]"
            :column="column"
            :is-sortable="isSortable"
            :disabled="disabled"
            :checkbox-selected="selectedCheckboxes.includes(column.key)"
            :radio-selected="column.key === (selectedRadio as string)"
            :sort-order="sorting && sorting[column.key]"
            :merged-headers="getMergedHeadersForColumn?.(column.key) ?? []"
            @checkbox="(value, item) => $emit('checkbox', value, item)"
            @radio="(value, item) => $emit('radio', value, item)"
            @click="$emit('cellClick', column.key)"
            @sort="onSort"
          />
        </slot>
      </th>
    </tr>
  </thead>
</template>

<script setup lang="ts">
import { toRaw } from 'vue'

import LTableHeaderCell from './LTableHeaderCell.vue'
import type { ParentSlots, TableDataColumn, TableSortDirection } from './types'

type Props = {
  /** Data used to build the different columns for the table and setting up of some
   * default styles for each one of them */
  columns: TableDataColumn[]
  /** Enables drag-and-drop row reordering handle column */
  hasSortableRows?: boolean
  /** Defines if the table will have multiple selection capabilities */
  hasMultiSelect?: boolean
  /** Defines if the data will be sortable by column */
  isSortable?: boolean
  /** Internal prop to give the ability to pass slot contents programatically */
  parentSlots?: ParentSlots
  /** Defines if the sorting can be made by more than one column or not */
  hasMultiSort?: boolean
  /** Hides the Header row */
  hideHeaders?: boolean
  /** Indicates whether the functionality or component is disabled */
  disabled?: boolean
  /** Function to get merged headers for a column */
  getMergedHeadersForColumn?: (columnKey: string) => string[]
}

const props = withDefaults(defineProps<Props>(), {
  hasSortableRows: false,
  hasMultiSelect: false,
  isSortable: false,
  parentSlots: () => ({}),
  selectedCheckboxes: () => [],
  selectedRadio: '',
  hasMultiSort: false,
  hideHeaders: false,
  disabled: false
})

/** Keeps track of the selected checkboxes among all header cells */
const selectedCheckboxes = defineModel<string[]>('selectedCheckboxes', {
  default: []
})
/** Keeps track of the selected radio among all header cells */
const selectedRadio = defineModel<string>('selectedRadio')

const sorting = defineModel<Record<string, TableSortDirection>>('sorting', {
  default: {}
})

const emit = defineEmits<{
  cellClick: [columnKey: string]
  checkbox: [boolean, TableDataColumn]
  radio: [boolean, TableDataColumn]
}>()

function onSort(key: string, direction: TableSortDirection): void {
  let tmpSorting = toRaw(sorting.value)
  if (props.hasMultiSort) {
    tmpSorting[key] = direction?.toUpperCase() as TableSortDirection
  } else {
    tmpSorting = { [key]: direction?.toUpperCase() as TableSortDirection }
  }

  for (const key in tmpSorting) {
    if (tmpSorting[key] === undefined) {
      delete tmpSorting[key]
    }
  }

  sorting.value = tmpSorting
}

function renderParentHeadSlot(column: TableDataColumn) {
  const slot = props.parentSlots[`head(${column.key})`] as
    | ((props: Record<string, unknown>) => unknown)
    | undefined
  if (!slot) return null
  const mergedHeaders = props.getMergedHeadersForColumn?.(column.key) ?? []
  const sortDirection = sorting.value[column.key]
  const checkboxSelected = selectedCheckboxes.value.includes(column.key)
  const radioSelected = column.key === (selectedRadio.value as string)
  const sortOrder = sorting.value && sorting.value[column.key]

  return slot({
    column,
    mergedHeaders,
    'merged-headers': mergedHeaders,
    sortDirection,
    'sort-direction': sortDirection,
    isSortable: props.isSortable,
    'is-sortable': props.isSortable,
    disabled: props.disabled,
    checkboxSelected,
    'checkbox-selected': checkboxSelected,
    radioSelected,
    'radio-selected': radioSelected,
    sortOrder,
    'sort-order': sortOrder,
    onCheckbox: (value: boolean, item: TableDataColumn) => emit('checkbox', value, item),
    onRadio: (value: boolean, item: TableDataColumn) => emit('radio', value, item),
    onClick: () => emit('cellClick', column.key),
    onSort
  })
}
</script>
